import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  EventEmitter,
  inject,
  model,
  OnInit,
  Output,
  QueryList,
  signal,
  ViewChild,
  ViewChildren,
  DOCUMENT,
  AfterViewInit
} from '@angular/core'
import {
  BehaviorSubject,
  combineLatest,
  delay,
  filter,
  finalize,
  fromEvent,
  map,
  Observable,
  skip,
  Subscription,
  take,
  tap,
  timer
} from 'rxjs'
import { CaregiverResponse } from '@biot-client/biot-client-organization'
import { NzDrawerPlacement } from 'ng-zorro-antd/drawer'
import { Store } from '@ngxs/store'
import { AlertState } from '../../../../../store/alert/alert.state'
import { NavigationEnd, Router } from '@angular/router'
import { AlertDTO, AlertSeverity } from '../../../../../shared/model/alert'
import { EntityDictionary } from '@angular-ru/cdk/entity'
import { DepartmentState } from '../../../../../store/department/department.state'
import { RootStore } from '../../../../../store/root-store'
import {
  NzModalRef,
  NzModalComponent,
  NzModalContentDirective,
  NzModalTitleDirective
} from 'ng-zorro-antd/modal'
import { PreferenceState } from '../../../../../store/preference/preference.state'
import { UserState } from '../../../../../store/user/user.state'
import { UserInterface } from '../../../../../shared/model/user.model'
import { DepartmentDTO } from '../../../../../shared/model/permission.model'
import { environment } from '../../../../../environments/environment'
import { PccState } from '../../../../../store/pcc/pcc.state'
import { AsyncPipe } from '@angular/common'
import { AuthState } from 'projects/aiomed/src/store/auth/auth.state'
import {
  defaultAllDepartment,
  DepartmentType
} from 'projects/aiomed/src/shared/model/departments.model'
import {
  ADMIN_DESKTOP_ITEMS,
  AUTOMATIC_DEPARTMENT_ITEMS,
  CNA_SIDEMENU_ITEMS,
  MY_PLAN_ITEM,
  SEMI_AUTOMATIC_DEPARTMENT_ITEMS
} from '../../constants/sidemenu-items.constant'
import { FeatureFlagService } from 'projects/aiomed/src/app/personal-assistant/access/feature-flag.service'
import { hasResolvableClinicalRole } from 'projects/aiomed/src/app/personal-assistant/data/pa-clinical-role.predicate'
import { DeviceState } from 'projects/aiomed/src/store/device/device.state'
import { DeviceDTO } from 'projects/aiomed/src/shared/model/device.model'
import { ErrorState } from 'projects/aiomed/src/store/error/error.state'
import {
  FormBuilder,
  Validators,
  ReactiveFormsModule,
  FormsModule
} from '@angular/forms'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { NzInputNumberComponent } from 'ng-zorro-antd/input-number'
import { adminPinCode } from '../../validators/admin-code.validator'
import { NzInputDirective } from 'ng-zorro-antd/input'
import { ReportState } from 'projects/aiomed/src/store/report/report.state'
import { NotificationState } from '../../../../../store/notification/notification.state'
import { NetworkService } from 'projects/aiomed/src/shared/services/network.service'
import { ListItem, SideMenuComponent } from 'design-system'
import { ɵNzTransitionPatchDirective } from 'ng-zorro-antd/core/transition-patch'
import { NzIconDirective } from 'ng-zorro-antd/icon'
import { NzButtonComponent } from 'ng-zorro-antd/button'
import { NzWaveDirective } from 'ng-zorro-antd/core/wave'
import { LoginTabletNurseComponent } from '../../../../pages/auth/components/login/login-tablet-nurse/login-tablet-nurse.component'
import { NzRowDirective, NzColDirective } from 'ng-zorro-antd/grid'
import { NzFormItemComponent, NzFormLabelComponent } from 'ng-zorro-antd/form'
import { NzSelectComponent, NzOptionComponent } from 'ng-zorro-antd/select'
import { UserPopoverComponent } from '../../../modals/components/user-popover/user-popover.component'
import { ReportIssueComponent } from '../../../modals/components/report-issue/report-issue.component'
import { WarningModalComponent } from '../../../mobile-ionic-components/component/warning-modal/warning-modal.component'
import { PaQaDevtoolsComponent } from '../../../../_qa-devtools'
import { ReceivingDataDirective } from '../../receiving-data.directive'
import { isUserClasifiedAsCNA } from 'projects/aiomed/src/core/helpers/roles'
import { PatientState } from 'projects/aiomed/src/store/patient/patient.state'
import { OrganizationState } from 'projects/aiomed/src/store/organization/organization.state'
import { ZoomVideoState } from 'projects/aiomed/src/store/zoom-video/zoom-video.state'
import { LogrocketPFFacade } from 'projects/aiomed/src/shared/services/logrocket-pf-facade.service'
import { UserActivityService } from 'projects/aiomed/src/shared/services/user-activity/user-activity.service'
import { OfflineReportingState } from 'projects/aiomed/src/store/offline-reporting/offline-reporting.state'
import MeasurementServicePlugin from 'projects/aiomed/src/shared/capacitor/measurement/measurements-plugin'
import { RouteName } from 'projects/aiomed/src/core/enums/route-name.enum'
import { TourService } from 'projects/aiomed/src/app/features/tour-guide'

@Component({
  selector: 'aiomed-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SideMenuComponent,
    ɵNzTransitionPatchDirective,
    NzIconDirective,
    NzButtonComponent,
    NzWaveDirective,
    NzModalComponent,
    NzModalContentDirective,
    LoginTabletNurseComponent,
    NzModalTitleDirective,
    NzRowDirective,
    NzFormItemComponent,
    NzColDirective,
    NzFormLabelComponent,
    NzSelectComponent,
    ReactiveFormsModule,
    FormsModule,
    NzOptionComponent,
    NzInputNumberComponent,
    UserPopoverComponent,
    ReportIssueComponent,
    WarningModalComponent,
    PaQaDevtoolsComponent,
    ReceivingDataDirective,
    AsyncPipe
  ]
})
export class MenuComponent implements OnInit, AfterViewInit {
  private readonly router = inject(Router)
  private readonly departmentState = inject(DepartmentState)
  private readonly document = inject<Document>(DOCUMENT)
  private readonly authState = inject(AuthState)
  private readonly changeDetectorRef = inject(ChangeDetectorRef)
  private readonly reportState = inject(ReportState)
  private readonly networkService = inject(NetworkService)
  private readonly tourService = inject(TourService)
  private readonly preferenceState = inject(PreferenceState)
  private readonly pccState = inject(PccState)
  private readonly patientState = inject(PatientState)
  private readonly destroyRef = inject(DestroyRef)
  private readonly store = inject(Store)
  private readonly logrocketPFFacade = inject(LogrocketPFFacade)
  private readonly userActivityService = inject(UserActivityService)
  private readonly offlineReportingState = inject(OfflineReportingState)
  private readonly featureFlag = inject(FeatureFlagService)

  @ViewChild('adminPinCodeField') adminPinCodeField: NzInputNumberComponent
  @ViewChildren(NzInputDirective) pccLoginInputs: QueryList<NzInputDirective>
  @Output('collapseMenuEmitter') collapseMenu = new EventEmitter()
  user$: Observable<UserInterface | null> = inject(Store).select(
    UserState.currentUser
  )
  userLoading$: Observable<boolean> = inject(Store).select(UserState.isLoading)
  isAuthLoading$: Observable<boolean> = inject(Store).select(
    AuthState.isLoading
  )
  devicesAlertsCount$: Observable<number> = inject(Store).select(
    RootStore.devicesAlertsCount
  )
  patientsAlertsCount$: Observable<number> = inject(Store).select(
    RootStore.patientsAlertsCount
  )
  unReadStatusCount$: Observable<number> = inject(Store).select(
    NotificationState.unReadStatus
  )
  alerts$: Observable<EntityDictionary<string, AlertDTO>> = inject(
    Store
  ).select(AlertState.alerts)
  preferenceSoundNotifications$: Observable<null | boolean> = inject(
    Store
  ).select(PreferenceState.preferenceSoundNotifications)
  isCollapsedMenu$: Observable<null | boolean> = inject(Store).select(
    PreferenceState.preferenceIsCollapseMenu
  )
  pccLoginWasNotPerformedButClosed$: Observable<boolean> = inject(Store).select(
    AuthState.pccLoginWasNotPerformedButClosed
  )
  protected readonly departments = this.store.selectSignal(
    DepartmentState.allDepartmentsCurrentOrganization
  )
  protected readonly currentDepartments$: Observable<DepartmentDTO[] | null> =
    this.store.select(DepartmentState.currentDepartments)
  withoutDepartment$: Observable<boolean> = inject(Store).select(
    DepartmentState.withoutDepartment
  )
  currentUserDevice$ = inject(Store).select(DeviceState.currentUserDevice)
  secondaryDevicesIds$: Observable<string[] | null> = inject(Store).select(
    PreferenceState.secondaryDevicesIds
  )
  language$ = inject(Store).select(PreferenceState.language)
  isUserCNA$: Observable<boolean> = inject(Store).select(UserState.isUserCNA)
  isUserRN$: Observable<boolean> = inject(Store).select(UserState.isUserRN)
  protected readonly hasCNAAccessToken = this.store.selectSignal(
    AuthState.hasCNAAccessToken
  )
  isPccLogging$: Observable<boolean> = inject(Store).select(
    AuthState.isPccLogging
  )
  isPCCAccountUsagePopupVisible$: Observable<boolean> = inject(Store).select(
    AuthState.isPCCAccountUsagePopupVisible
  )
  departmentType$ = inject(Store).select(DepartmentState.departmentType)
  errorMessage$: Observable<string | null> = inject(Store).select(
    ErrorState.errorMessage
  )
  incorrectTimeSystem$: Observable<boolean> = inject(Store).select(
    PreferenceState.incorrectTimeSystem
  )
  sharedDevices$: Observable<DeviceDTO[]> = inject(Store).select(
    DeviceState.sharedDevices
  )
  isForcedResolution$ = inject(Store).select(PreferenceState.isForcedResolution)
  patientsRooms = inject(Store).selectSignal(PatientState.patientsRooms)
  selectedPatientsRooms = inject(Store).selectSignal(PatientState.selectedRooms)
  userShouldWatchVideoTutorial = inject(Store).selectSignal(
    UserState.isShouldWatchVideoTutorial
  )
  hasVirtualKeyboard = inject(Store).selectSignal(
    PreferenceState.hasVirtualKeyboard
  )
  isPccUser$: Observable<boolean> = inject(Store).select(UserState.isPccUser)

  private readonly isMatrixCareUser$ = this.store.select(
    UserState.isMatrixCareUser
  )

  protected readonly inSession = this.store.selectSignal(
    ZoomVideoState.inSession
  )
  protected readonly emrConfig = this.store.selectSignal(
    OrganizationState.emrConfig
  )
  protected readonly isSessionExpired = this.store.selectSignal(
    PreferenceState.sessionExpire
  )
  protected readonly offlineReportingPatientsIds = this.store.selectSignal(
    OfflineReportingState.offlineReportingPatientsIds
  )
  protected readonly isLogoutConfirmModalOpened = this.store.selectSignal(
    PreferenceState.isLogoutConfirmModalOpened
  )
  protected readonly currentReleaseVersion = this.store.selectSignal(
    RootStore.currentReleaseVersion
  )
  protected readonly isUserAdmin = this.store.selectSignal(
    UserState.isUserAdmin
  )
  protected readonly isSelectNetworkPopupShown = this.store.selectSignal(
    PreferenceState.isSelectNetworkPopupShown
  )
  protected readonly isSimulationMode = this.store.selectSignal(
    PreferenceState.isSimulationMode
  )
  protected readonly isOffline$ = this.networkService.isOfflineDebounced$
  protected readonly isOnline$ = this.networkService.isOnlineObs$
  protected readonly isOnline = toSignal(this.isOnline$)

  private readonly screenSaver = this.store.selectSignal(
    PreferenceState.screenSaver
  )

  placement: NzDrawerPlacement = 'left'
  request: XMLHttpRequest = new XMLHttpRequest()
  context: AudioContext = new AudioContext()
  source: AudioBufferSourceNode
  timerSub: Subscription
  confirmModal?: NzModalRef
  source$: Observable<Event>
  popoverVisible: boolean = false
  reportIssueModalVisible: boolean = false
  public readonly DepartmentType = DepartmentType
  sourceClick$: Observable<Event> = fromEvent(window, 'click')
  sourceTouchmove$: Observable<Event> = fromEvent(window, 'mousemove')
  sourceTouchStart$: Observable<Event> = fromEvent(window, 'touchstart')
  public sideMenuItems$!: Observable<ListItem[]>
  public isPccTokenInvalid: boolean = false
  public isDeviceChoosing: boolean = false
  public isMobile = this.preferenceState.isMobile
  public passwordVisible: boolean = false
  public selectedDeviceId: string = ''
  public selectedSecondaryDevicesIds: string[] = []
  public selectedRooms: string[] = []
  public hasFocus = signal<boolean>(false)
  loginWithCred: boolean = false
  protected isShowDevicePairing: boolean = false
  private deviceUpdateSubject = new BehaviorSubject<boolean>(false)
  readonly deviceUpdate$ = this.deviceUpdateSubject.asObservable()
  private readonly formBuilder = inject(FormBuilder)
  public adminPinCode = this.formBuilder.control('', {
    validators: [adminPinCode],
    updateOn: 'change'
  })
  public sendPCCUsageConsent = this.formBuilder.group({
    confirmPassword: this.formBuilder.control('', {
      validators: [Validators.required, Validators.minLength(6)]
    }),
    allow: this.formBuilder.control(false, {
      validators: [Validators.required]
    })
  })
  protected selectedDepartmentsIds = model<string[] | null>(null)
  protected readonly firstSelectedDepartmentNameIfOnlyOneSelected = computed(
    () => {
      const selectedDepartmentsIds = this.selectedDepartmentsIds()
      if (!selectedDepartmentsIds) {
        return ''
      }
      return this.departments().find(
        department => department.id === selectedDepartmentsIds[0]
      )?.name
    }
  )
  protected readonly defaultAllDepartment = signal(defaultAllDepartment)
  protected readonly isEnteringSimulationMode = signal<boolean>(false)

  get allow() {
    return this.sendPCCUsageConsent.get('allow')
  }

  get confirmPassword() {
    return this.sendPCCUsageConsent.get('confirmPassword')
  }

  ngOnInit(): void {
    this.initializeListeners()
    this.setAudioContext()
    this.localStoreListener()
    this.setSidenavItems()
    this.detectUserRoleChanges()
    this.setUserPasswordAfterPCCLogin()
    this.incorrectTimeSystemListener()
    this.setCurrentUserDeviceAndDepartmentIds()
    this.setSelectedRooms()
    if (this.isMobile) {
      this.handleTabletPccLoginFocusOut()
      this.listenForOnlineChangesToHideSelectNetworkPopup()
    }
  }

  public ngAfterViewInit(): void {
    this.propagateDocumentClickForScreenSaverLogicStart()
  }

  localStoreListener() {
    this.source$ = fromEvent(window, 'storage')
    this.source$
      .pipe(delay(500), takeUntilDestroyed(this.destroyRef))
      .subscribe((data: Event) => {
        if (data instanceof StorageEvent) {
          if (
            data.key === null ||
            (data.key === 'auth.accessJwt' &&
              data.newValue === null &&
              data.oldValue != null)
          ) {
            // this.systemLogOut()
          }
        }
      })
  }

  initializeListeners(): void {
    this.preferenceSoundNotifications$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        if (!data || !environment.production) return
        this.setMediaSourceWhenHaveCriticalAlert()
      })
    this.withoutDepartment$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(i => {
        if (!i) return
        // this.handlerClickLogout()
      })
  }

  setMediaSourceWhenHaveCriticalAlert(): void {
    this.alerts$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((alerts: EntityDictionary<string, AlertDTO>) => {
        const alertsDTO: AlertDTO[] = Object.values(alerts)
        const alert: AlertDTO | undefined = alertsDTO.find(
          alert => alert.severity === AlertSeverity.Critical
        )
        if (!alert || !alertsDTO.length) {
          if (this.timerSub) {
            this.timerSub.unsubscribe()
            this.request.abort()
          }
          return
        }
        navigator.mediaDevices
          .getUserMedia({ audio: true })
          .then(stream => {
            this.timerSub = timer(0, 50000)
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe(() => {
                this.source.start(0)
                setTimeout(() => {
                  this.source.stop(0)
                  this.request.abort()
                  this.setAudioContext()
                }, 2000)
              })
          })
          .catch(error => console.warn(error))
      })
  }

  setAudioContext() {
    this.source = this.context.createBufferSource()
    this.source.connect(this.context.destination)
    this.request.open('GET', 'assets/door_bell.aac', true)
    this.request.timeout = 10000
    this.request.responseType = 'arraybuffer'
    this.request.onload = () => {
      this.context.decodeAudioData(
        this.request.response,
        response => {
          this.source.buffer = response
          this.source.loop = true
        },
        function () {
          console.error('The request failed.')
        }
      )
    }
    this.request.send()
  }

  close(): void {
    this.collapseMenu.emit(false)
  }

  public handlerClickLogout(): void {
    this.authState.setIsLogouting(true)
    this.hasFocus.set(false)
    this.preferenceState.setIsLogoutConfirmModalOpened(false)
    this.offlineReportingState.resetState()
    this.authState.logout(true)
  }

  public openConfirmLogout(): void {
    combineLatest([this.isUserCNA$, this.isPccUser$, this.isMatrixCareUser$])
      .pipe(take(1))
      .subscribe(([isUserCNA, isPccUser, isMatrixCareUser]) => {
        if (isUserCNA && !isPccUser && !isMatrixCareUser) {
          this.preferenceState.setIsLogoutConfirmModalOpened(true)

          timer(150)
            .pipe(take(1))
            .subscribe(() => {
              this.adminPinCodeField.focus()
            })
        } else {
          this.handlerClickLogout()
        }
      })
  }

  public handleIsWarningAboutNotSavedOrSentMeasurementsVisible(): void {
    const hasAnyOfflineReportingSetsPending =
      this.offlineReportingPatientsIds().length
    if (!hasAnyOfflineReportingSetsPending) {
      this.openConfirmLogout()
      return
    }
    this.reportState.setIsWarningAboutNotSavedOrSentMeasurementsVisible(true)
  }

  public handlerClickRnLogout(): void {
    this.authState.rnLogout()
    this.preferenceState.setPreferenceIsCollapseMenu(true)
    this.reportState.setSuccessfullySentCurrentShiftPatientAllMeasurements(null)
    this.reportState.setIsPatientVitalsTakenPopupVisible(false)
    this.offlineReportingState.resetState()
  }

  public exitSimulationMode(): void {
    this.preferenceState.setIsSimulationMode(false)
  }

  public openEnterSimulationModePopup(): void {
    this.isEnteringSimulationMode.set(true)
    setTimeout(() => {
      this.adminPinCodeField.focus()
    }, 200)
  }

  adminCodeFocus() {
    this.hasFocus.set(true)
    this.changeDetectorRef.detectChanges()
  }

  adminCodeBlur() {
    setTimeout(() => {
      if (
        !this.document.activeElement ||
        this.document.activeElement.tagName !== 'INPUT'
      ) {
        this.hasFocus.set(false)
        this.changeDetectorRef.detectChanges()
      }
    }, 50)
  }

  handlerChangeLanguageEmitter($event: string) {
    this.preferenceState.setLanguage($event)
  }

  openReportIssueModal() {
    this.reportIssueModalVisible = true
    this.popoverVisible = false
  }

  closeReportIssueModal() {
    this.reportIssueModalVisible = false
  }

  reportIssueToLogRocket(event: string) {
    this.closeReportIssueModal()
    this.logrocketPFFacade.logrocketCaptureMessage(event, {
      userReport: 'user-report'
    })
  }

  public itemClick(): void {
    this.collapseMenu.emit(true)
  }

  public showPopover(): void {
    this.popoverVisible = true
  }

  public toggleSidenav(event: boolean): void {
    this.collapseMenu.emit(event)
  }

  public showModalLoginToPcc(): void {
    this.authState.setIsPccLogging(true)
  }

  public hideModalLoginToPcc(): void {
    this.authState.setIsPccLogging(false)
    this.hasFocus.set(false)
    this.loginWithCred = false
    this.authState.setIsLoading(false)
    this.loginWithCred = false
    this.closeOpenedWindow()
  }

  public closeOpenedWindow(): void {
    this.authState.closeOpenedWindow()
  }

  public handlerFormSubmitEmitter(credentials: {
    username: string
    password: string
  }): void {
    if (!this.isOnline()) {
      this.preferenceState.setIsSelectNetworkPopupShown(true)
      return
    }
    this.handleRnLogin(credentials)
    this.hasFocus.set(false)
  }

  public reLoginToRnMode(): void {
    this.authState.setIsPccLogging(true)
    this.showModalLoginToPcc()
    this.setSessionExpired(false)
  }

  public closeLoggedOutModal(): void {
    this.setSessionExpired(false)
  }

  public refreshPage(): void {
    location.reload()
  }

  public openPccLoginWindow(): void {
    this.authState.openPccLoggingWindowAfterInitialLogin(
      this.authState.getState().accessJwt?.token!
    )
    this.isPccTokenInvalid = false
  }

  public loginWithPcc(): void {
    if (!this.isOnline()) {
      this.preferenceState.setIsSelectNetworkPopupShown(true)
      return
    }
    this.authState.loginWithPcc('tablet')
  }

  public selectNetwork(): void {
    MeasurementServicePlugin.openWiFiSettings()
    this.preferenceState.setIsSelectNetworkPopupShown(false)
  }

  public hidePCCAccountUsagePopup(): void {
    this.authState.setIsPCCAccountUsagePopupVisible(false)
  }

  public allowPCCAccountUsage(): void {
    this.authState.allowPccAccountUsage()
    this.authState.setIsPCCAccountUsagePopupVisible(false)
  }

  public pairDevices(): void {
    this.isShowDevicePairing = true
    setTimeout(() => {
      this.adminPinCodeField.focus()
    }, 200)
  }

  public hideDevicePairing(): void {
    this.isShowDevicePairing = false
    this.isDeviceChoosing = false
    this.adminPinCode.reset()
    this.adminPinCode.updateValueAndValidity()
    this.preferenceState.setIsLogoutConfirmModalOpened(false)
    this.setCurrentUserDeviceAndDepartmentIds()
    this.setSelectedRooms()
    this.isEnteringSimulationMode.set(false)
  }

  public showDeviceChoosing(): void {
    this.isDeviceChoosing = true
    this.hasFocus.set(false)
  }

  public showSpotlightTable(): void {
    this.preferenceState.setPreferenceIsCollapseMenu(true)
    this.reportState.getTabletSpotlightPatients()
  }

  public setCurrentDeviceAndDepartmentIds(): void {
    if (this.selectedRooms) {
      this.patientState.setPatientsRooms(this.selectedRooms)
    }
    if (this.selectedSecondaryDevicesIds) {
      this.preferenceState.setSecondaryDevicesIds(
        this.selectedSecondaryDevicesIds
      )
    }
    const departmentIds = this.selectedDepartmentsIds()?.filter(
      departmentId => departmentId !== defaultAllDepartment.id
    )
    this.preferenceState.setCurrentDeviceId(this.selectedDeviceId)
    if (departmentIds && departmentIds.length) {
      this.departmentState.setCurrentDepartment(departmentIds)
    }
    this.isDeviceChoosing = false
    this.hideDevicePairing()

    this.user$.pipe(take(1)).subscribe(user => {
      if (!user?.id) {
        return
      }
      const preferenceDepartment = this.preferenceState.getState().department
      const currentDepartments = Array.isArray(preferenceDepartment)
        ? preferenceDepartment.map(department => department.id).join(', ')
        : preferenceDepartment?.id
      const isUserAdmin = this.store.selectSnapshot(UserState.isUserAdmin)
      const logrocketTrackProperties = {
        name: user.name.firstName + ' ' + user.name.lastName,
        email: user.email!,
        _degree:
          (isUserAdmin
            ? ('RPM' as CaregiverResponse.DegreeEnum)
            : user._degree) || '',
        mainDevice: this.preferenceState.getState().deviceId || '',
        rooms: this.selectedRooms.join(',') || '',
        devices:
          this.preferenceState.getState().secondaryDevicesIds?.join(',') || '',
        onDutyDepartment: currentDepartments || ''
      }
      this.logrocketPFFacade.logrocketIdentify(
        user?.id,
        logrocketTrackProperties
      )
      this.logrocketPFFacade.logrocketTrack('user', logrocketTrackProperties)
      this.logrocketPFFacade.logrocketLog('user', logrocketTrackProperties)
    })
    this.collapseMenu.emit(true)
  }

  public handleEnterClick(event: Event): void {
    if (!this.adminPinCode.valid) {
      return
    }
    if (this.isEnteringSimulationMode()) {
      this.confirmEnteringSimulationMode()
      return
    }
    if (!this.isLogoutConfirmModalOpened()) {
      this.showDeviceChoosing()
      return
    }
    this.handlerClickLogout()
  }

  public allItemClick(): void {
    const selectedDepartments = this.selectedDepartmentsIds()
    if (selectedDepartments?.includes(defaultAllDepartment.id)) {
      this.selectedDepartmentsIds.set([defaultAllDepartment.id])
    } else {
      this.selectedDepartmentsIds.set(
        this.departments().map(department => department.id)
      )
    }
  }

  public departmentSelectChange(departmentsIds: string[]): void {
    const filteredDepartmentsIds = departmentsIds.filter(
      departmentId => departmentId !== defaultAllDepartment.id
    )
    if (filteredDepartmentsIds.length !== this.departments().length) {
      this.selectedDepartmentsIds.update(departmentsIds =>
        !departmentsIds
          ? []
          : departmentsIds?.filter(
              departmentId => departmentId !== defaultAllDepartment.id
            )
      )
    }
    if (filteredDepartmentsIds.length === this.departments().length) {
      this.selectedDepartmentsIds.update(departmentsIds =>
        departmentsIds ? [...departmentsIds, defaultAllDepartment.id] : []
      )
    }
  }

  public setSessionExpired(isExpired: boolean): void {
    this.preferenceState.setSessionExpire(isExpired)
  }

  public handlerDeviceUpdatesEmitter(): void {
    this.deviceUpdateSubject.next(true)
    this.userActivityService.setDeviceUpdateSubject(true)
  }

  public navigateToProfile(): void {
    this.router.navigateByUrl('/' + RouteName.MonthlySummary)
  }

  public confirmEnteringSimulationMode(): void {
    this.isEnteringSimulationMode.set(false)
    this.preferenceState.setIsSimulationMode(true)
    this.preferenceState.setPreferenceIsCollapseMenu(true)
    this.adminPinCode.reset()
    this.adminPinCode.updateValueAndValidity()
    this.changeDetectorRef.detectChanges()
    this.tourService.stop()
  }

  private setSidenavItems(): void {
    this.sideMenuItems$ = combineLatest([
      this.departmentType$,
      this.user$,
      this.unReadStatusCount$
    ]).pipe(
      map(([departmentType, user, unReadStatusCount]) => {
        if (!user) {
          return []
        }

        if (this.isUserAdmin()) {
          if (this.isMobile) {
            return CNA_SIDEMENU_ITEMS
          }
          // isUserAdmin() here means an RPM user (`_userType: 'organization-user'`), the only user
          // type the PA serves. "My Plan" is prepended only when the user passes the full PA gate,
          // which ships dark: a resolvable clinical role (the same predicate as paFlagGuard and the
          // role resolver), the env flag and the per-user allowlist. ADMIN_DESKTOP_ITEMS is the RPM
          // user's "Patients" menu.
          const rpmItems = ADMIN_DESKTOP_ITEMS(unReadStatusCount)
          const u = user as { id?: string; _id?: string }
          return hasResolvableClinicalRole(user) &&
            this.featureFlag.isPaUnlockedFor(u.id ?? u._id ?? null)
            ? [MY_PLAN_ITEM, ...rpmItems]
            : rpmItems
        }

        if (isUserClasifiedAsCNA(user?._degree)) {
          return CNA_SIDEMENU_ITEMS
        }

        if (this.isMobile) {
          return CNA_SIDEMENU_ITEMS
        }

        if (departmentType === DepartmentType.Automatic) {
          return AUTOMATIC_DEPARTMENT_ITEMS(unReadStatusCount)
        } else {
          return SEMI_AUTOMATIC_DEPARTMENT_ITEMS(unReadStatusCount)
        }
      })
    )
  }

  private detectUserRoleChanges(): void {
    if (!this.isMobile) {
      return
    }
    combineLatest([this.user$, this.isUserCNA$])
      .pipe(
        filter(([user]) => !!user),
        skip(1),
        tap(([user, isUserCNA]) => {
          if (!user) {
            return
          }
          if (!isUserCNA) {
            this.hideModalLoginToPcc()
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe()
  }

  private handleRnLogin(credentials: {
    username: string
    password: string
  }): void {
    this.authState
      .rnLogin(credentials)
      .pipe(tap(() => this.offlineReportingState.resetState()))
  }

  private setUserPasswordAfterPCCLogin(): void {
    this.authState.rnPasswordObservable$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(password => {
        this.confirmPassword?.setValue(password)
        this.allow?.setValue(false)
      })
  }

  private setCurrentUserDeviceAndDepartmentIds(): void {
    this.selectedDeviceId = ''
    this.selectedDepartmentsIds.set([])
    this.selectedSecondaryDevicesIds = []

    this.currentUserDevice$.pipe(take(1)).subscribe(currentUserDevice => {
      if (currentUserDevice && currentUserDevice.id !== '-') {
        this.selectedDeviceId = currentUserDevice.id
      }
    })
    this.currentDepartments$
      .pipe(
        filter(v => !!v),
        take(1)
      )
      .subscribe(currentDepartments => {
        if (currentDepartments && currentDepartments.length) {
          const isAllDepartmentsSelected =
            currentDepartments.length === this.departments().length
          const departmentsIdsToBeSet = isAllDepartmentsSelected
            ? [
                ...currentDepartments.map(department => department.id),
                defaultAllDepartment.id
              ]
            : currentDepartments.map(department => department.id)
          this.selectedDepartmentsIds.set(departmentsIdsToBeSet)
        }
      })
    this.secondaryDevicesIds$
      .pipe(take(1))
      .subscribe(selectedSecondaryDevicesIds => {
        if (selectedSecondaryDevicesIds && selectedSecondaryDevicesIds.length) {
          this.selectedSecondaryDevicesIds = [...selectedSecondaryDevicesIds]
        }
      })
  }

  private setSelectedRooms(): void {
    this.selectedRooms = this.selectedPatientsRooms()
  }

  private incorrectTimeSystemListener() {
    this.incorrectTimeSystem$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        if (!data) return
        this.handlerClickLogout()
      })
  }

  private handleTabletPccLoginFocusOut(): void {
    this.pccLoginWasNotPerformedButClosed$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(pccLoginWasNotPerformedButClosed => {
        if (pccLoginWasNotPerformedButClosed) {
          this.pccLoginInputs.forEach(inp =>
            inp['elementRef'].nativeElement.blur()
          )
          this.hasFocus.set(false)
          this.changeDetectorRef.detectChanges()
        }
      })
  }

  private propagateDocumentClickForScreenSaverLogicStart(): void {
    if (this.screenSaver()) {
      return
    }

    this.document.documentElement.click()
  }

  private listenForOnlineChangesToHideSelectNetworkPopup(): void {
    this.isOnline$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(isOnline => {
        if (!isOnline) {
          return
        }
        if (this.isSelectNetworkPopupShown()) {
          this.preferenceState.setIsSelectNetworkPopupShown(false)
        }
      })
  }
}
